import React, { useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiCheck, FiMessageSquare } from "react-icons/fi";

import type { SurveyQuestion } from "../../lib/api";

type Props = {
  title: string;
  description: string;
  questions: SurveyQuestion[];
  /** Index the builder wants shown; the preview follows the question being edited. */
  focusIndex: number;
  onFocusIndexChange: (index: number) => void;
};

type Screen = "intro" | "question" | "complete";

/**
 * Renders the survey the way the user will see it, in a phone frame.
 *
 * Styled in the admin's dark palette so it sits in the builder without glaring, while
 * keeping the prototype's structure: one question per screen, a progress bar, and
 * tap-to-insert suggestion chips.
 *
 * NOTE: this project is on Tailwind 3.2, which has no `size-*` utility — use explicit
 * `h-`/`w-` pairs here or the element silently collapses.
 */
const SurveyPreview: React.FC<Props> = ({
  title,
  description,
  questions,
  focusIndex,
  onFocusIndexChange,
}) => {
  const [screen, setScreen] = useState<Screen>("question");
  // Local answers exist only so the preview feels real; nothing is submitted.
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const safeIndex = Math.min(
    Math.max(focusIndex, 0),
    Math.max(0, questions.length - 1)
  );
  const question = questions[safeIndex];

  useEffect(() => {
    if (questions.length === 0) setScreen("intro");
  }, [questions.length]);

  const answer = question ? answers[question.key] ?? "" : "";
  const setAnswer = (value: string) =>
    question && setAnswers((prev) => ({ ...prev, [question.key]: value }));

  const appendSuggestion = (text: string) => {
    if (!question) return;
    const current = answers[question.key] ?? "";
    if (current.includes(text)) return;
    // Appends rather than replaces — matches the prototype's chip behaviour.
    setAnswer(
      current.trim() ? `${current.replace(/[.\s]+$/, "")}. ${text}` : text
    );
  };

  const options = useMemo(() => {
    if (!question?.options) return [];
    const list = question.options.filter((o) => o.label.trim());
    return question.allowOther
      ? [...list, { id: "__other__", label: "Other" }]
      : list;
  }, [question]);

  return (
    <div className="sticky top-4">
      <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
        Preview — how users will see it
      </p>

      <div className="mx-auto w-full max-w-[340px] overflow-hidden rounded-[28px] border-[6px] border-[#232C33] bg-[#161E25] shadow-[0_18px_50px_rgba(0,0,0,0.5)]">
        <div className="flex h-[560px] flex-col font-manrope">
          {screen === "intro" && (
            <div className="flex flex-1 flex-col items-center px-5 pb-6 pt-14 text-center">
              <span className="flex h-[92px] w-[92px] items-center justify-center rounded-full bg-secondary_button/10">
                <span className="flex h-[68px] w-[68px] items-center justify-center rounded-full bg-secondary_button text-white">
                  <FiMessageSquare className="h-7 w-7" />
                </span>
              </span>
              <h2 className="mt-5 max-w-[250px] text-[20px] font-extrabold uppercase leading-[1.15] text-white">
                {title.trim() || "Help us make DeeX better"}
              </h2>
              <p className="mt-2.5 max-w-[250px] text-[13px] leading-[1.6] text-[#869AA9]">
                {description.trim() ||
                  "This short survey helps us understand how you use DeeX."}
              </p>
              <button
                type="button"
                onClick={() => setScreen("question")}
                disabled={questions.length === 0}
                className="mt-auto w-full rounded-xl bg-secondary_button py-3 text-[14px] font-semibold text-white transition disabled:opacity-40"
              >
                Begin survey
              </button>
            </div>
          )}

          {screen === "complete" && (
            <div className="flex flex-1 flex-col items-center px-5 pb-6 pt-14 text-center">
              <span className="flex h-[92px] w-[92px] items-center justify-center rounded-full bg-emerald-500/10">
                <span className="flex h-[68px] w-[68px] items-center justify-center rounded-full bg-emerald-500 text-white">
                  <FiCheck className="h-8 w-8" />
                </span>
              </span>
              <h2 className="mt-5 text-[20px] font-extrabold uppercase leading-[1.15] text-white">
                Survey complete
              </h2>
              <p className="mt-2.5 max-w-[250px] text-[13px] leading-[1.6] text-[#869AA9]">
                Thank you for sharing your experience.
              </p>
              <button
                type="button"
                onClick={() => {
                  setScreen("question");
                  onFocusIndexChange(0);
                }}
                className="mt-auto w-full rounded-xl border border-secondary_button py-3 text-[14px] font-semibold text-secondary_button"
              >
                Review answers
              </button>
            </div>
          )}

          {screen === "question" &&
            (questions.length === 0 || !question ? (
              <div className="flex flex-1 items-center justify-center px-8 text-center">
                <p className="text-[13px] leading-relaxed text-[#5C6B76]">
                  Add a question and it will appear here exactly as users will
                  see it.
                </p>
              </div>
            ) : (
              <div className="flex flex-1 flex-col">
                <div className="flex items-center gap-3 px-4 pb-3 pt-4">
                  <button
                    type="button"
                    onClick={() =>
                      safeIndex === 0
                        ? setScreen("intro")
                        : onFocusIndexChange(safeIndex - 1)
                    }
                    aria-label="Previous"
                    className="text-[#C6D0D7] transition hover:text-white"
                  >
                    <FiArrowLeft className="h-5 w-5" />
                  </button>
                  <span className="text-[11px] font-semibold tabular-nums text-white">
                    {safeIndex + 1}/{questions.length}
                  </span>
                  <span className="h-1 flex-1 overflow-hidden rounded-full bg-[#2C363E]">
                    <span
                      className="block h-full rounded-full bg-secondary_button transition-[width]"
                      style={{
                        width: `${((safeIndex + 1) / questions.length) * 100}%`,
                      }}
                    />
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto px-4">
                  <h2 className="pt-2 text-[17px] font-bold leading-[1.35] text-white">
                    {question.prompt.trim() || "Your question will appear here"}
                  </h2>

                  {(question.type === "SINGLE_CHOICE" ||
                    question.type === "MULTI_CHOICE") && (
                    <div className="flex flex-col gap-2 pt-4">
                      {options.length === 0 && (
                        <p className="text-[13px] text-[#5C6B76]">
                          Add options to see them here.
                        </p>
                      )}
                      {options.map((option) => {
                        const selected =
                          question.type === "MULTI_CHOICE"
                            ? answer.split(",").includes(option.id)
                            : answer === option.id;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                              if (question.type === "MULTI_CHOICE") {
                                const current = answer
                                  ? answer.split(",").filter(Boolean)
                                  : [];
                                setAnswer(
                                  current.includes(option.id)
                                    ? current
                                        .filter((x) => x !== option.id)
                                        .join(",")
                                    : [...current, option.id].join(",")
                                );
                              } else {
                                setAnswer(option.id);
                              }
                            }}
                            className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left text-[14px] transition ${
                              selected
                                ? "border-secondary_button bg-secondary_button/15 font-semibold text-white"
                                : "border-[#2C363E] bg-[#1C2227] text-[#C6D0D7] hover:border-[#3A4852]"
                            }`}
                          >
                            <span
                              className={`h-4 w-4 shrink-0 border-2 ${
                                question.type === "MULTI_CHOICE"
                                  ? "rounded-[4px]"
                                  : "rounded-full"
                              } ${
                                selected
                                  ? "border-secondary_button bg-secondary_button"
                                  : "border-[#3A4852]"
                              }`}
                            />
                            {option.label}
                          </button>
                        );
                      })}

                      {question.allowOther && answer === "__other__" && (
                        <input
                          type="text"
                          placeholder={
                            question.otherPlaceholder || "Tell us more..."
                          }
                          className="mt-1 w-full rounded-xl border border-secondary_button bg-[#1C2227] px-3.5 py-3 text-[14px] text-white outline-none placeholder:text-[#5C6B76]"
                        />
                      )}
                    </div>
                  )}

                  {(question.type === "SHORT_TEXT" ||
                    question.type === "LONG_TEXT") && (
                    <div className="pt-4">
                      <textarea
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        placeholder={
                          question.placeholder || "Type your answer..."
                        }
                        maxLength={question.maxLength ?? 600}
                        className={`w-full resize-none border-b border-[#2C363E] bg-transparent py-2 text-[14px] leading-[1.6] text-white outline-none placeholder:text-[#5C6B76] focus:border-secondary_button ${
                          question.type === "LONG_TEXT"
                            ? "min-h-[120px]"
                            : "min-h-[52px]"
                        }`}
                      />
                      <p className="pt-1 text-right text-[11px] tabular-nums text-[#5C6B76]">
                        {answer.length}/{question.maxLength ?? 600}
                      </p>
                      {question.suggestions &&
                        question.suggestions.length > 0 && (
                          <div className="pt-3">
                            <p className="text-[11px] text-[#869AA9]">
                              Or start from one of these
                            </p>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {question.suggestions.map((s) => (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => appendSuggestion(s.label)}
                                  className="rounded bg-secondary_button/15 px-2 py-0.5 text-[13px] font-semibold text-secondary_button transition hover:bg-secondary_button/25"
                                >
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  {question.type === "NPS" && (
                    <div className="pt-4">
                      <div className="grid grid-cols-6 gap-1.5">
                        {Array.from({ length: 11 }, (_, n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setAnswer(String(n))}
                            className={`aspect-square rounded text-[14px] font-semibold tabular-nums transition ${
                              answer === String(n)
                                ? "bg-secondary_button text-white"
                                : "bg-secondary_button/15 text-secondary_button hover:bg-secondary_button/25"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-between pt-2 text-[11px] text-[#869AA9]">
                        <span>{question.scale?.minLabel || "Not likely"}</span>
                        <span>{question.scale?.maxLabel || "Very likely"}</span>
                      </div>
                    </div>
                  )}

                  {question.type === "RATING" && (
                    <div className="pt-4">
                      <div className="flex gap-2">
                        {Array.from(
                          {
                            length:
                              (question.scale?.max ?? 5) -
                              (question.scale?.min ?? 1) +
                              1,
                          },
                          (_, i) => {
                            const value = (question.scale?.min ?? 1) + i;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setAnswer(String(value))}
                                className={`flex h-12 flex-1 items-center justify-center rounded-xl text-[15px] font-semibold tabular-nums transition ${
                                  Number(answer) >= value
                                    ? "bg-secondary_button text-white"
                                    : "bg-secondary_button/15 text-secondary_button hover:bg-secondary_button/25"
                                }`}
                              >
                                {value}
                              </button>
                            );
                          }
                        )}
                      </div>
                      <div className="flex justify-between pt-2 text-[11px] text-[#869AA9]">
                        <span>{question.scale?.minLabel || "Poor"}</span>
                        <span>{question.scale?.maxLabel || "Excellent"}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-4 pb-5 pt-3">
                  <button
                    type="button"
                    onClick={() =>
                      safeIndex === questions.length - 1
                        ? setScreen("complete")
                        : onFocusIndexChange(safeIndex + 1)
                    }
                    className="w-full rounded-xl bg-secondary_button py-3 text-[14px] font-semibold text-white transition hover:brightness-110"
                  >
                    {safeIndex === questions.length - 1 ? "Submit" : "Continue"}
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="mt-3 flex justify-center gap-2">
        {(["intro", "question", "complete"] as Screen[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScreen(s)}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold capitalize transition ${
              screen === s
                ? "border-secondary_button bg-secondary_button text-white"
                : "border-[#2C363E] bg-[#1C2227] text-[#869AA9] hover:text-[#C6D0D7]"
            }`}
          >
            {s === "question" ? "Questions" : s}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SurveyPreview;
